/**
 * Export et import de la base.
 *
 * Les donnees ne quittent jamais l'appareil autrement que par ce fichier, que
 * l'utilisateur declenche et range lui-meme. C'est aussi la seule protection
 * contre la perte de l'iPhone : il n'y a pas de serveur pour rejouer l'historique.
 */

import { decoderPayload } from '../analysis/sync'
import { nowInstant } from '../domain/time'
import type { Evenement, Nuit, Reglages } from '../domain/types'
import { META_DERNIER_EXPORT, db, ecrireMeta, lireMeta } from './db'
import { ingererEvenements, lireReglages, recalculerNuits } from './repo'

const FORMAT = 'suivi-sommeil'
const VERSION = 1

export interface Sauvegarde {
  format: string
  version: number
  exporteLe: string
  reglages: Reglages
  evenements: Evenement[]
  /** Seules les nuits corrigees sont sauvegardees : les autres se rededuisent
   *  des evenements, les inclure ferait grossir le fichier sans rien ajouter. */
  nuitsCorrigees: Nuit[]
}

export async function exporter(): Promise<Sauvegarde> {
  const [reglages, evenements, nuits] = await Promise.all([
    lireReglages(),
    db.evenements.toArray(),
    db.nuits.toArray(),
  ])
  await ecrireMeta(META_DERNIER_EXPORT, nowInstant())
  return {
    format: FORMAT,
    version: VERSION,
    exporteLe: nowInstant(),
    reglages,
    evenements,
    nuitsCorrigees: nuits.filter((n) => n.corrigee),
  }
}

export function nomFichierExport(): string {
  return `sommeil-${nowInstant().slice(0, 10)}.json`
}

export async function dateDernierExport(): Promise<string | null> {
  return lireMeta<string | null>(META_DERNIER_EXPORT, null)
}

export interface ResultatImport {
  evenements: number
  nuits: number
}

/**
 * Fusionne une sauvegarde dans la base courante.
 *
 * Fusion et non remplacement : importer une sauvegarde d'il y a trois mois ne
 * doit pas effacer les nuits depuis. Les evenements se dedoublonnent par leur
 * identifiant, et les nuits corrigees importees ecrasent leur homologue locale
 * — une correction explicite l'emporte toujours sur une deduction.
 */
export async function importer(brut: unknown): Promise<ResultatImport> {
  if (!brut || typeof brut !== 'object') throw new Error('Fichier illisible.')
  const s = brut as Partial<Sauvegarde>
  if (s.format !== FORMAT) {
    throw new Error("Ce fichier n'est pas une sauvegarde de cette application.")
  }
  if (typeof s.version !== 'number' || s.version > VERSION) {
    throw new Error('Sauvegarde produite par une version plus recente.')
  }

  const evenements = Array.isArray(s.evenements) ? s.evenements : []
  const nuits = Array.isArray(s.nuitsCorrigees) ? s.nuitsCorrigees : []

  if (evenements.length > 0) await db.evenements.bulkPut(evenements)
  if (nuits.length > 0) await db.nuits.bulkPut(nuits)
  await recalculerNuits()

  return { evenements: evenements.length, nuits: nuits.length }
}

/**
 * Import du fichier tampon des raccourcis (`nuit.txt` ou `historique.txt`).
 *
 * Le filet quand la synchro du matin a echoue — URL rejetee, telephone repose
 * verrouille. Volontairement manuel : il ne devrait servir que deux fois par
 * an, et l'automatiser demanderait un serveur.
 */
export async function importerTampon(texte: string): Promise<ResultatImport> {
  const { evenements, ignores } = decoderPayload(texte)
  const resultat = await ingererEvenements(evenements, ignores)
  return { evenements: resultat.nouveaux, nuits: 0 }
}

/** Efface tout. Irreversible, et sans serveur pour rejouer quoi que ce soit. */
export async function effacerTout(): Promise<void> {
  await db.transaction('rw', db.evenements, db.nuits, db.meta, async () => {
    await db.evenements.clear()
    await db.nuits.clear()
    await db.meta.clear()
  })
}
